import { useEffect, useRef } from 'react';
import type { Screen } from './useGame.js';

/**
 * URL ↔ screen routing, History-API based (no router dependency).
 *
 * Only NAVIGABLE screens own a URL — the places a user can land directly or
 * reach with back/forward:
 *
 *   /            home
 *   /play        matchmaking search
 *   /room/CODE   join a friend's room by code  ← the invite link
 *   /stats       stats
 *   /how         how it works
 *   /lab         game lab (dev)
 *
 * MATCH screens (countdown / race / result) are ephemeral: they're driven by
 * live server events and can't be resumed from a URL, so they render over the
 * current route without owning a path. When a match ends, we're back at the
 * navigable route the URL already shows.
 */

export type Route =
  | { name: 'home' }
  | { name: 'play' }
  | { name: 'room'; code: string }
  | { name: 'stats' }
  | { name: 'how' }
  | { name: 'lab' };

/** The screens that map onto a URL (everything else is ephemeral / in-match). */
const SCREEN_TO_PATH: Partial<Record<Screen, string>> = {
  home: '/',
  searching: '/play',
  stats: '/stats',
  rules: '/how',
  lab: '/lab',
  // 'room' is handled specially (needs the code): see pathForState.
};

/** Parse the current location into a Route (defaults to home). */
export function parseRoute(pathname: string, search: string): Route {
  const path = pathname.replace(/\/+$/, '') || '/';

  // Legacy invite: /?room=CODE → treat as /room/CODE.
  const legacy = new URLSearchParams(search).get('room');
  if (legacy) return { name: 'room', code: legacy.toUpperCase() };

  if (path === '/' || path === '') return { name: 'home' };
  if (path === '/play') return { name: 'play' };
  if (path === '/stats') return { name: 'stats' };
  if (path === '/how') return { name: 'how' };
  // /lab is a dev-only tool and isn't shipped in the prod build. In production,
  // fall through so a direct hit lands home rather than on an empty Lab screen.
  if (path === '/lab' && import.meta.env.DEV) return { name: 'lab' };

  const room = path.match(/^\/room\/([^/]+)$/);
  if (room?.[1]) return { name: 'room', code: decodeURIComponent(room[1]).toUpperCase() };

  return { name: 'home' };
}

/** The URL a navigable screen should show, or null if the screen owns no URL. */
export function pathForState(screen: Screen, roomCode: string | null): string | null {
  // Both the host ('room') and a joiner ('join-room') live at /room/CODE.
  if ((screen === 'room' || screen === 'join-room') && roomCode) return `/room/${roomCode}`;
  return SCREEN_TO_PATH[screen] ?? null;
}

/** Absolute, shareable invite URL for a room code. */
export function inviteUrl(code: string): string {
  return `${window.location.origin}/room/${encodeURIComponent(code)}`;
}

/**
 * Keeps the browser URL in sync with the current screen, and turns back/forward
 * (and the initial URL) into navigation via `onRoute`.
 *
 * `onRoute(route, initial)` is called for the first load and every popstate.
 * The caller maps a Route to the appropriate state action.
 */
export function useRouter(
  screen: Screen,
  roomCode: string | null,
  onRoute: (route: Route, initial: boolean) => void,
): void {
  const onRouteRef = useRef(onRoute);
  onRouteRef.current = onRoute;

  // First load + browser back/forward.
  useEffect(() => {
    onRouteRef.current(parseRoute(window.location.pathname, window.location.search), true);
    const handler = (): void => {
      onRouteRef.current(parseRoute(window.location.pathname, window.location.search), false);
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // Reflect state → URL. Only push when the screen maps to a (different) URL, so
  // ephemeral match screens leave the URL untouched.
  //
  // The FIRST commit is skipped: on load the URL is already the source of truth
  // (the initial-route effect above drives state FROM it), so syncing state→URL
  // here would fight that and leave a spurious history entry.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    const target = pathForState(screen, roomCode);
    if (target === null) return; // ephemeral screen — keep the current URL
    if (window.location.pathname === target && !window.location.search) return;
    window.history.pushState({}, '', target);
  }, [screen, roomCode]);
}
